"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.FUNNEL_SOURCE = exports.FUNNEL_SCHEMA_VERSION = exports.FUNNEL_EVENTS = exports.FLOW_TYPE = void 0;
exports.logFunnelEvent = logFunnelEvent;
exports.logPerceived = logPerceived;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _constants = require("../constants/constants");
var _utils = require("../utils/utils");
var _bwaEventLogger = _interopRequireDefault(require("./bwa-event-logger"));
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2["default"])(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Katal funnel events — a single, extensible module for BWA_EVENT funnel logging.
 *
 * Today it carries checkout-flow events; add any future funnel event (PDP viewed,
 * order placed, ATC, etc.) as a new entry in FUNNEL_EVENTS — nothing else changes.
 *
 * Every event is emitted with the SAME context schema as BwAHorizonteWebAppAssets, so
 * both log sources stitch offline by checkoutId (primary) / bwaSessionId (fallback).
 * `stage` and `flowType` are OPTIONAL — checkout events set them; standalone events
 * (e.g. pdp_page_viewed) may omit them.
 *
 * Journey ordering at stitch time is by `stage` (clock-independent), then eventTime.
 */

var FUNNEL_SCHEMA_VERSION = exports.FUNNEL_SCHEMA_VERSION = "1";
var FUNNEL_SOURCE = exports.FUNNEL_SOURCE = "external_assets";
var FLOW_TYPE = exports.FLOW_TYPE = {
  MANDATE: "mandate",
  QUICK_CHECKOUT: "quick_checkout"
};

/**
 * Registry of all funnel events. name -> { name, stage?, flowType? }.
 * Mirrored in BwAHorizonteWebAppAssets — keep event names + stages in sync.
 * Extend by adding entries (future: PDP_PAGE_VIEWED, ORDER_PLACED, ...).
 */
var FUNNEL_EVENTS = exports.FUNNEL_EVENTS = {
  // --- Checkout: mandate flow ---
  MANDATE_BUTTON_CLICKED: {
    name: "mandate_button_clicked",
    stage: 1,
    flowType: FLOW_TYPE.MANDATE
  },
  MANDATE_REDIRECT_STALLED: {
    name: "mandate_redirect_stalled",
    stage: 3.5
  },
  MANDATE_FALLBACK_POPUP_SHOWN: {
    name: "mandate_fallback_popup_shown",
    stage: 3.5
  },
  MANDATE_FALLBACK_POPUP_CLICKED: {
    name: "mandate_fallback_popup_clicked",
    stage: 3.5
  },
  // --- Checkout: quick-checkout flow ---
  QC_BUTTON_CLICKED: {
    name: "qc_button_clicked",
    stage: 1,
    flowType: FLOW_TYPE.QUICK_CHECKOUT
  }
  // --- Future events go here (no other change needed), e.g.:
  // PDP_PAGE_VIEWED: { name: "pdp_page_viewed" },
  // ORDER_PLACED:    { name: "order_placed" },
};

/**
 * Coarse network class from the Network Information API (Chromium/Android).
 * iOS/Safari expose no navigator.connection -> "unknown".
 */
function networkClass(conn) {
  if (!conn) return "unknown";
  if (conn.saveData) return "save-data";
  return conn.effectiveType || "unknown";
}

/** Low-end heuristic. deviceMemory/cpuCores are Chromium-only (null on iOS). */
function isLowEndDevice(deviceMemory, cpuCores) {
  if (typeof deviceMemory === "number" && deviceMemory <= 2) return true;
  if (typeof cpuCores === "number" && cpuCores <= 4) return true;
  return false;
}

/** OS version from the UA string (Android x.y / iOS x_y). null when unknown. */
function getOsVersion(ua, os) {
  try {
    if (os === "android") {
      var m = ua.match(/Android\s+([\d.]+)/i);
      return m ? m[1] : null;
    }
    if (os === "ios") {
      var _m = ua.match(/OS\s+(\d+[_\d]*)/i);
      return _m ? _m[1].replace(/_/g, ".") : null;
    }
  } catch (e) {
    // fall through
  }
  return null;
}
function getDeviceContext() {
  var nav = typeof navigator !== "undefined" ? navigator : {};
  var deviceMemory = typeof nav.deviceMemory === "number" ? nav.deviceMemory : null;
  var cpuCores = typeof nav.hardwareConcurrency === "number" ? nav.hardwareConcurrency : null;
  var ua = nav.userAgent || "";
  var os = (0, _utils.isAndroid)() ? "android" : /iPad|iPhone|iPod/i.test(ua) ? "ios" : "other";
  return {
    type: /Mobile|Android|iPhone/i.test(ua) ? "mobile" : "desktop",
    os: os,
    osVersion: getOsVersion(ua, os),
    userAgent: ua,
    browser: (0, _utils.detectBrowser)(ua),
    isMeta: (0, _utils.isMetaBrowser)(),
    isMshop: false,
    // external merchant page is never an mShop webview
    cpuCores: cpuCores,
    deviceMemory: deviceMemory,
    iosTier: null,
    // iOS screen-geometry tier is derived on the Horizonte side
    isLowEnd: isLowEndDevice(deviceMemory, cpuCores),
    screenW: typeof screen !== "undefined" ? screen.width : null,
    screenH: typeof screen !== "undefined" ? screen.height : null,
    dpr: typeof window !== "undefined" ? window.devicePixelRatio : null
  };
}
function getNetworkContext() {
  var conn = typeof navigator !== "undefined" && (navigator.connection || navigator.mozConnection || navigator.webkitConnection) || null;
  return {
    effectiveType: conn && conn.effectiveType ? conn.effectiveType : null,
    measuredClass: networkClass(conn),
    downlinkMbps: conn && typeof conn.downlink === "number" ? conn.downlink : null,
    rtt: conn && typeof conn.rtt === "number" ? conn.rtt : null,
    saveData: !!(conn && conn.saveData)
  };
}
function getBwaSessionId() {
  try {
    return (0, _utils.getBwaMutantSessionId)() || null;
  } catch (e) {
    return null;
  }
}
function getStoreDomain() {
  try {
    return window.location.hostname;
  } catch (e) {
    return null;
  }
}

/**
 * Build the shared context envelope common to every funnel event.
 * @param {object} extras  event-specific overrides ({ checkoutId, mode, clientClickTime, flowType, detail })
 */
function buildFunnelContext(extras) {
  var now = Date.now();
  return _objectSpread(_objectSpread({
    schemaVersion: FUNNEL_SCHEMA_VERSION,
    source: FUNNEL_SOURCE,
    checkoutId: extras.checkoutId != null ? extras.checkoutId : null,
    bwaSessionId: getBwaSessionId(),
    eventTime: now,
    eventTimeIso: new Date(now).toISOString(),
    // Single client-origin anchor for clock-safe app-switch latency (prereq #6).
    clientClickTime: extras.clientClickTime != null ? extras.clientClickTime : now,
    ecp: _constants.ECP,
    shopId: window.storeId != null ? window.storeId : null,
    storeDomain: getStoreDomain(),
    // Two distinct experiment fields (D16). External knows the RTO/mandate treatment;
    // revampedAspTreatment is only known on the Horizonte side (null here).
    rtoTreatment: (0, _utils.getRtoTreatment)() || null,
    revampedAspTreatment: null
  }, (0, _utils.getEventExperimentFields)()), {}, {
    mode: extras.mode != null ? extras.mode : null,
    device: getDeviceContext(),
    network: getNetworkContext()
  });
}

/**
 * Emit a funnel event to Katal.
 * @param {{name: string, stage?: number, flowType?: string}} event  one of FUNNEL_EVENTS
 * @param {object} extras  { checkoutId, mode, clientClickTime, flowType, detail } — flowType
 *                         overrides the registry default; stage always comes from the registry.
 */
function logFunnelEvent(event) {
  var extras = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  // Telemetry MUST NEVER block the primary checkout action. This runs on the critical
  // path (e.g. before triggerMandateCheckout/triggerQuickCheckout) and touches several
  // browser APIs (navigator.deviceMemory/connection, screen, ...); a top-level guard
  // ensures any unexpected failure is swallowed instead of breaking checkout.
  try {
    if (!event || !event.name) {
      return;
    }
    var fields = buildFunnelContext(extras);
    // stage/flowType are optional — set only when the event defines them.
    if (event.stage != null) fields.stage = event.stage;
    var flowType = extras.flowType != null ? extras.flowType : event.flowType;
    if (flowType != null) fields.flowType = flowType;
    if (extras.detail) fields.detail = extras.detail;
    _bwaEventLogger["default"].logEvent(event.name, fields);
  } catch (e) {
    // Swallow — funnel telemetry failure must not affect the shopper.
    try {
      console.error("BwA funnel event failed (ignored):", e);
    } catch (ignored) {
      // console may be unavailable; nothing more to do.
    }
  }
}

/**
 * Emit one `bwa_<widget>_perceived` Katal event measuring the shopper's wait from the merchant
 * page's navigationStart to the widget content painting. All marks use performance.now(), which is
 * monotonic and already navigationStart-relative — skew-free and needs no Shopify theme change.
 *
 * @param {string} widget        widget name, e.g. "delivery_block", "quick_checkout"
 * @param {Object} opts
 * @param {number} [opts.tInjected] performance.now() captured when the widget element was inserted
 * @param {string} [opts.signalType] "content_visible" (iframe posted a content-backed *_RENDERED)
 *                                    or "injection" (only element-in-DOM is known)
 * @param {Object} [opts.extra]   extra fields merged into the event payload (e.g. is_in_stock)
 */
function logPerceived(widget) {
  var _perf$tScript, _perf$tValidated;
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref$tInjected = _ref.tInjected,
    tInjected = _ref$tInjected === void 0 ? null : _ref$tInjected,
    _ref$signalType = _ref.signalType,
    signalType = _ref$signalType === void 0 ? "content_visible" : _ref$signalType,
    _ref$extra = _ref.extra,
    extra = _ref$extra === void 0 ? {} : _ref$extra;
  var perf = window.__bwaPerf || {};
  var tScript = (_perf$tScript = perf.tScript) !== null && _perf$tScript !== void 0 ? _perf$tScript : null;
  var tValidated = (_perf$tValidated = perf.tValidated) !== null && _perf$tValidated !== void 0 ? _perf$tValidated : null;
  var tInDom = performance.now();

  // rAF is the closest we can get to "pixels visible to the shopper" from script.
  requestAnimationFrame(function () {
    var tPainted = performance.now();
    _bwaEventLogger["default"].logEvent("bwa_".concat(widget, "_perceived"), _objectSpread({
      widget: widget,
      ecp_store_id: window.storeId,
      perceivedMs: Math.round(tPainted),
      inDomMs: Math.round(tInDom),
      paintGapMs: Math.round(tPainted - tInDom),
      jsBootMs: tScript != null ? Math.round(tScript) : null,
      validateMs: tScript != null && tValidated != null ? Math.round(tValidated - tScript) : null,
      widgetMs: tInjected != null ? Math.round(tPainted - tInjected) : null,
      signal_type: signalType
    }, extra));
  });
}
//# sourceMappingURL=katal-funnel-events.js.map
